import { useEffect, useState } from 'react';
import baseAxios from 'src/utils/baseAxios';
import { getUser } from 'src/utils/Common';

/**
 * MailOrbit "bu tenant için açık mı" kapısı (SR-019).
 *
 * Karar SiriusApi'den gelir: GET api/MailOrbitFeature -> { enabled: true|false } (SR-017).
 * Sonuç oturum (kullanıcı+tenant) başına bir kez çekilir ve modül içinde saklanır. Redux'a
 * konmadı: store redux-persist ile localStorage'a yazıldığı için eski bir "açık" değeri bir
 * sonraki oturuma sızardı.
 *
 * Kapalı VEYA henüz yüklenmemişken `enabled` false'tur; MailOrbit UI'ı görünmez ve hiçbir
 * MailOrbit isteği atılmaz. Hata durumunda da kapalı sayılır (fail closed).
 *
 * Email2Case çakışma kuralı SiriusApi'de uygulanır (Email2Case tenant'ı için enabled=false döner);
 * ekran yalnızca endpoint sonucuna güvenir, istemci tarafında ayrıca kontrol yoktur.
 *
 * Mock modu (REACT_APP_MAILORBIT_MOCK=true): ağ çağrısı yapılmaz, kapı `enabled: true` döner.
 *
 * Kaldırılan env değişkenleri (SR-019): REACT_APP_MAILORBIT_ACTIVE_TENANTS,
 * REACT_APP_MAILORBIT_TENANT_MAP, REACT_APP_MAILORBIT_TOKEN_SOURCE, REACT_APP_MAILORBIT_API_URL.
 */

const FEATURE_PATH = '/MailOrbitFeature';

const isMock = () => process.env.REACT_APP_MAILORBIT_MOCK === 'true';

// status: 'idle' | 'loading' | 'ready'
let state = { key: null, status: 'idle', enabled: false, autoCreateWorkOrder: true };
let inflight = null;
const listeners = new Set();

const setState = next => {
  state = next;
  listeners.forEach(l => l(state));
};

const sessionKey = () => {
  const user = getUser();
  if (!user) return null;
  return `${user.TenantId}:${user.UserId}`;
};

// autoCreateWorkOrder (SR-023/SR-024): alan yoksa true (eski SiriusApi yanıtı ile uyum).
const fetchFeature = async () => {
  if (isMock()) return { enabled: true, autoCreateWorkOrder: true };
  try {
    const res = await baseAxios.get(FEATURE_PATH);
    const data = (res && res.data) || {};
    return { enabled: data.enabled === true, autoCreateWorkOrder: data.autoCreateWorkOrder !== false };
  } catch (e) {
    return { enabled: false, autoCreateWorkOrder: true };
  }
};

/** Oturum başına bir kez yükler; aynı oturumda tekrar çağrılırsa mevcut sonucu kullanır. */
export const loadMailOrbitFeature = () => {
  const key = sessionKey();
  if (!key) return Promise.resolve(false);
  if (state.key === key && state.status !== 'idle') {
    return inflight && state.status === 'loading' ? inflight : Promise.resolve(state.enabled);
  }
  setState({ key, status: 'loading', enabled: false, autoCreateWorkOrder: true });
  const p = fetchFeature().then(({ enabled, autoCreateWorkOrder }) => {
    if (state.key === key) setState({ key, status: 'ready', enabled, autoCreateWorkOrder });
    return enabled;
  });
  inflight = p;
  return p;
};

/** Senkron anlık görüntü (UI dışı kod için). Yüklenmediyse veya oturum değiştiyse false. */
export const getMailOrbitEnabledSnapshot = () => {
  const key = sessionKey();
  return !!key && state.key === key && state.status === 'ready' && state.enabled;
};

/** { enabled, loaded, autoCreateWorkOrder }: yönlendirme kararı için loaded gerekir (yüklenirken yönlendirme yapma). */
export const useMailOrbitFeature = () => {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force(n => n + 1);
    listeners.add(l);
    loadMailOrbitFeature();
    return () => listeners.delete(l);
  }, []);
  const key = sessionKey();
  const current = !!key && state.key === key;
  return {
    enabled: current && state.status === 'ready' && state.enabled,
    loaded: current && state.status === 'ready',
    autoCreateWorkOrder: !current || state.autoCreateWorkOrder !== false,
  };
};

export const useMailOrbitEnabled = () => useMailOrbitFeature().enabled;
